import { AxiosInstance } from "axios"

import type { TransactionResponse } from "../../../../../modules/payments/transactionTypes"

import {
    CreateTakeawayOrderRequest,
    TakeawayDeliveryLocationsResponse,
    TakeawayKitchensResponse,
    TakeawayMenuResponse,
    TakeawayOrderDelivery,
    TakeawayPaymentDetailsResponse,
    TakeawayProductFavoritesResponse,
} from "./takeawayTypes"

export enum TakeawayQuery {
    TAKEAWAY_MENU = "takeaway-menu",
    TAKEAWAY_USER_PRODUCTS = "takeaway-user-products",
    TAKEAWAY_USER_SHOPS = "takeaway-user-shops",
    TAKEAWAY_PAYMENT_DETAILS = "takeaway-payment-details",
    TAKEAWAY_FAVORITES = "takeaway-favorites",
    TAKEAWAY_DELIVERY_LOCATIONS = "takeaway-delivery-locations",
}

export enum TakeawayMutation {
    CREATE_ORDER = "takeaway-create-order",
}

type TakeawayMenuParams = Readonly<{
    kitchenId: number
    date: string
}>

export async function fetchTakeawayMenu(
    { kitchenId, date }: TakeawayMenuParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayMenuResponse> {
    const { data } = await axios.get(`suppliers/kitchens/${kitchenId}/menues/takeaway`, {
        params: { date },
        signal,
    })
    return data
}

type TakeawayUserProductsParams = Readonly<{
    userId: number
    webshopId: number
    date: string
}>

export async function fetchUserTakeawayProducts(
    { userId, webshopId, date }: TakeawayUserProductsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayMenuResponse> {
    const { data } = await axios.get(`users/${userId}/shops/${webshopId}/products`, {
        params: { date },
        signal,
    })
    return data
}

type TakeawayPaymentDetailsParams = Readonly<{
    kitchenId: number
    deliveries: TakeawayOrderDelivery[]
}>

export async function fetchTakeawayPaymentDetails(
    { kitchenId, deliveries }: TakeawayPaymentDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayPaymentDetailsResponse> {
    const url = `suppliers/kitchens/${kitchenId}/payment/paymentDetails/takeaway`
    const { data } = await axios.post(url, { deliveries }, { signal })
    return data
}

type CreateTakeawayOrderParams = Readonly<{
    request: CreateTakeawayOrderRequest
}>

export async function createTakeawayOrder(
    { request }: CreateTakeawayOrderParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TransactionResponse> {
    const url = `orders/takeaway`
    const { data } = await axios.post(url, request, { signal })
    return data
}

type TakeawayFavoritesParams = Readonly<Record<string, never>>

export async function fetchProductFavorites(
    _: TakeawayFavoritesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayProductFavoritesResponse> {
    const { data } = await axios.get(`/organization/company/user/productFavorites`, { signal })
    return data
}

type TakeawayDeliveryLocationsParams = Readonly<{
    userId: number
    shopId: number
}>

export async function fetchTakeawayDeliveryLocations(
    { userId, shopId }: TakeawayDeliveryLocationsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayDeliveryLocationsResponse> {
    const { data } = await axios.get(`users/${userId}/shops/${shopId}/delivery/locations`, { signal })
    return data
}

export enum UserShopType {
    TAKEAWAY = "takeaway",
}

enum UserShopsQueryParam {
    TYPE = "type",
    MERCHANT_ID = "merchantId",
}

type FetchUserShopsParams = Readonly<{ userId: number; merchantId?: number; type: UserShopType }>

export async function fetchUserShops(
    { userId, merchantId, type }: FetchUserShopsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TakeawayKitchensResponse> {
    const params = new URLSearchParams({ [UserShopsQueryParam.TYPE]: type })
    if (merchantId) {
        params.set(UserShopsQueryParam.MERCHANT_ID, merchantId.toString())
    }
    const { data } = await axios.get(`users/${userId}/shops`, { params, signal })
    return data
}
